import React from "react"

import type { Message as MessageType } from "@/types/Transcript"

import { MessageContent } from "./MessageContent"
import { isMessageDimmed } from "./messageDimming"
import { MessageHeader } from "./MessageHeader"
import { messageSpeaker } from "./messageSpeaker"
import { MessageTiming } from "./MessageTiming"
import {
  isWebsocketEventMessage,
  SystemEventMessage,
} from "./SystemEventMessage"
import type { WordHighlight } from "./word-highlight"

interface MessageProps {
  message: MessageType
  index: number
  isSelected?: boolean
  /**
   * Draw the bubble at full width, left-aligned, instead of at the transcript's
   * four-fifths on its speaker's side. For a caller that shows one message on
   * its own, where the side carries no meaning and the bubble is the whole
   * surface.
   */
  fillWidth?: boolean
  onClick?: (index: number) => void
  className?: string
  highlightedMessages?: number[] | null
  highlightedWords?: WordHighlight[] | null
  agentLabel?: string
  userLabel?: string
  agentBgColor?: string
  userBgColor?: string
  agentAvatarUrl?: string
  userAvatarUrl?: string
  agentAvatarTint?: string
  userAvatarTint?: string
  /** The silence before this message, when an open latency metric measured one. */
  latencySeconds?: number
}

export const Message: React.FC<MessageProps> = ({
  message,
  index,
  isSelected = false,
  fillWidth = false,
  onClick,
  className = "",
  highlightedMessages = null,
  highlightedWords = null,
  agentLabel = "My Agent",
  userLabel = "Coval",
  agentBgColor = "bg-coval-chat-agent-surface",
  userBgColor = "bg-surface-1",
  agentAvatarUrl,
  userAvatarUrl,
  agentAvatarTint,
  userAvatarTint,
  latencySeconds,
}) => {
  const [areToolCallsExpanded, setAreToolCallsExpanded] = React.useState(false)

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent deselection when clicking on a message
    onClick?.(index)
  }

  const isDimmed = isMessageDimmed(highlightedMessages, index, isSelected)

  if (isWebsocketEventMessage(message)) {
    return (
      <SystemEventMessage
        message={message}
        index={index}
        isSelected={isSelected}
        onClick={onClick}
        className={`${className} ${isDimmed ? "opacity-45" : ""}`}
      />
    )
  }

  const speaker = messageSpeaker(message)
  const isUser = speaker === "persona"

  const toolCalls = message.tool_calls ?? []
  const hasToolCalls = toolCalls.length > 0
  const visibleToolCalls = areToolCallsExpanded
    ? toolCalls
    : toolCalls.slice(0, 1)
  const hiddenToolCallCount = toolCalls.length - visibleToolCalls.length

  return (
    <div
      data-message-index={index}
      className={`relative ${className} ${
        !isUser && !fillWidth ? "flex justify-end" : ""
      }`}
    >
      {/* Pointer convenience: a click anywhere on the bubble selects it. The
          header button is the keyboard and assistive-tech path to the same
          action, so the message text stays readable content. */}
      <div
        role="presentation"
        // A selection arrives from outside as often as from a click here — a
        // metric citing this turn, the playhead reaching it — so the bubble
        // says which one it is rather than leaving the reader to infer it from
        // where the pane happened to stop scrolling.
        className={`px-3.5 py-2.5 cursor-pointer rounded-lg mb-1.5 transition-all duration-150 ${
          fillWidth ? "w-full" : "w-4/5"
        } text-left p-4 ${isUser ? userBgColor : agentBgColor} ${
          isDimmed ? "opacity-45" : ""
        } ${isSelected ? "ring-2 ring-coval-contrast" : ""}`}
        onClick={handleClick}
      >
        <MessageHeader
          speaker={speaker}
          className="mb-1.5"
          agentLabel={agentLabel}
          userLabel={userLabel}
          agentAvatarUrl={agentAvatarUrl}
          userAvatarUrl={userAvatarUrl}
          agentAvatarTint={agentAvatarTint}
          userAvatarTint={userAvatarTint}
          onSelect={handleClick}
          isSelected={isSelected}
          messageNumber={index + 1}
        />
        {hasToolCalls ? (
          <div className="space-y-2">
            {visibleToolCalls.map((toolCall, toolCallIndex) => (
              <MessageContent
                key={toolCall.id ?? `tool-call-${toolCallIndex}`}
                content={toolCall}
              />
            ))}
            {hiddenToolCallCount > 0 && (
              <button
                type="button"
                className="text-xs text-coval-text-muted underline hover:text-coval-text transition-colors"
                aria-label={`Show ${hiddenToolCallCount} more tool calls`}
                onClick={(event) => {
                  event.stopPropagation()
                  setAreToolCallsExpanded(true)
                }}
              >
                +{hiddenToolCallCount} more
              </button>
            )}
          </div>
        ) : (
          <MessageContent
            content={message.content}
            highlightedWords={
              highlightedWords?.filter((h) => h.messageIndex === index) || null
            }
          />
        )}

        <MessageTiming message={message} latencySeconds={latencySeconds} />
      </div>
    </div>
  )
}
